<template>
  <div class="multi-address-select">
    <div
      v-if="expandAddresses && isMoreThanOneProperty"
      class="multi-address-select--show-addresses"
    >
      <div
        v-for="(property, idx) in orderedPropertyList"
        :key="property.property_id"
        :class="['multi-address-select--container', { 'not-first': idx }]"
        test-id="address-line"
        @click="switchAddress(property.property_id)"
        @mouseover="currentlyHovered = property.property_id"
        @mouseleave="currentlyHovered = null"
      >
        <LsIcon
          type="checkmark"
          :color="getIconColor(property.property_id)"
          size="md"
          class="multi-address-select--checkmark"
        />
        <div>
          <p class="multi-address-select--street">{{ property.street }}</p>
          <p v-if="property.info.multitenant.is_multitenant">
            {{ property.info.multitenant.units.join(', ') }}
          </p>
          <p>{{ property.city }}, {{ property.state }}, {{ property.zip }}</p>
        </div>
      </div>
    </div>

    <div v-else test-id="property-card" @click="expandAddress">
      <LsAddressCard
        :property="getSelectedProperty"
        :class="['multi-address-select--dropdown', { 'with-border': isMoreThanOneProperty }]"
        :show-chevron="isMoreThanOneProperty"
      />
    </div>
  </div>
</template>

<script>
import VueTypes from 'vue-types';
import _ from 'lodash';
import { LsIcon, LsAddressCard } from '@/components/common';
import { lsPropTypeHelpers } from 'ls-vue-common';

export default {
  name: 'LsMultiAddressSelect',
  components: {
    LsIcon,
    LsAddressCard,
  },
  props: {
    properties: VueTypes.arrayOf(
      VueTypes.shape({
        street: VueTypes.string.isRequired,
        info: VueTypes.shape({
          multitenant: VueTypes.shape({
            is_multitenant: VueTypes.custom(lsPropTypeHelpers.booleanOrNull).isRequired,
            units: VueTypes.arrayOf(VueTypes.string),
          }).isRequired.loose,
        }).isRequired.loose,
        city: VueTypes.string.isRequired,
        state: VueTypes.string.isRequired,
        zip: VueTypes.string.isRequired,
      }).isRequired.loose
    ).isRequired.loose,
    selectedPropertyId: VueTypes.number,
  },
  data() {
    return {
      expandAddresses: false,
      iconColor: 'primary',
      currentlyHovered: null,
    };
  },
  computed: {
    getSelectedProperty() {
      const selectedProperty = _.find(this.properties, {
        property_id: this.selectedPropertyId,
      });
      return selectedProperty || _.head(this.properties);
    },
    // always put the selected property first in the list
    orderedPropertyList() {
      const notSelectedArray = _.reject(this.properties, {
        property_id: this.selectedPropertyId,
      });
      return _.concat(this.getSelectedProperty, notSelectedArray);
    },
    isMoreThanOneProperty() {
      return this.properties.length > 1;
    },
  },
  methods: {
    expandAddress() {
      if (!this.isMoreThanOneProperty) {
        return;
      }
      this.expandAddresses = true;
    },
    getIconColor(property_id) {
      if (this.currentlyHovered === property_id && this.selectedPropertyId !== property_id) {
        return 'primary-light';
      }
      return this.selectedPropertyId === property_id ? 'primary' : 'white';
    },
    switchAddress(property_id) {
      this.expandAddresses = false;
      this.$emit('input', property_id);
    },
  },
};
</script>

<style lang="scss" scoped>
.multi-address-select {
  width: 100%;
  z-index: $z-index-multi-address-select;
}
.multi-address-select--show-addresses {
  cursor: pointer;
  border: 1px solid $primary-light;
  padding: 0 12px 0 12px;
}
.multi-address-select--dropdown {
  padding: 5px 10px 5px 20px;
  &.with-border {
    border: 1px solid $primary-light;
    cursor: pointer;
  }
}

.multi-address-select--container {
  height: 61px;
  padding: 0 15px 0 15px;
  color: $primary;
  display: flex;
  align-items: center;
  &.not-first {
    border-top: 1px solid $primary-light;
  }
  @media (max-width: $mobile-max-width) {
    min-width: 260px;
    padding: 0;
    flex-direction: row-reverse;
    justify-content: space-between;
  }
}
.multi-address-select--checkmark {
  margin-right: 20px;
  @media (max-width: $mobile-max-width) {
    margin-right: 0;
  }
}

.multi-address-select--street {
  max-width: 240px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: bold;
  font-size: $size-4;
}

.themed-app {
  .multi-address-select--show-addresses,
  .multi-address-select--dropdown,
  .multi-address-select--container.not-first {
    border-color: $theme-color-primary-light;
  }

  .multi-address-select--container {
    color: $theme-color-primary;
  }
}
</style>
